'use client';

import { Session } from 'next-auth';
import { useEffect, useMemo, useState } from 'react';

import {
    Alignable,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Icon,
    IconName,
    IconSize,
    Spacer,
    SpacerSize,
} from '@bytel/trilogy-react';

import { getSessionVariable } from '@services/storage';

import { getServerSession } from '@actions/get-session';

import { ShortListContext } from '@app-types/eligibility';
import { StoredHomepackProduct } from '@app-types/home-pack';
import { CategoryCodeInfo } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { PromoConfigurator } from '@components/promo-configurator';
import { RocCard } from '@components/roc-card';
import { SSIBlock } from '@components/ssi-block';
import { TabItem, Tabs } from '@components/tabs';

import { useMarketings } from '@hooks/use-marketings';

import { emitPriceContextChangeEvent } from '@helpers/marketings';
import { partials } from '@helpers/partials';

import { MarketingType } from '@constants/plan';
import { ELIGIBILITY_STORAGE_KEY, TECHNOLOGY } from '@constants/provider';

import { EligibilityOffer, EligibilityResults, JourneyConfigType, normalizeTechnology } from '@eligibility/helpers';

import { SelectedPackProductCard } from '../selected-pack-product-card';

const tabsOrder: string[] = [TECHNOLOGY.FTTH, TECHNOLOGY.BOX_5G, TECHNOLOGY.BOX_4G];

function getXGBoxLabel(results: EligibilityResults): string {
    let label = '4G box';
    const offers = results?.offers?.BOX_4G;
    if (!!offers) {
        const has4GBox = offers.some((o) => o.technology === TECHNOLOGY.BOX_4G);
        const has5GBox = offers.some((o) => o.technology === TECHNOLOGY.BOX_5G);
        if (has5GBox) {
            label = '5G box';
            if (has4GBox) {
                label = '4G / 5G box';
            }
        }
    }
    return label;
}

function getTechnologyName(technology: TECHNOLOGY, results: EligibilityResults): string | undefined {
    const technologyName: { [key in TECHNOLOGY]?: string } = {
        [TECHNOLOGY.FTTH]: 'Fibre',
        [TECHNOLOGY.BOX_4G]: getXGBoxLabel(results),
    };

    return technologyName[technology];
}

function sortOffersWithCategoryOrder(
    categoryCodeInfo: CategoryCodeInfo[],
    offers: EligibilityOffer[] = [],
): EligibilityOffer[] {
    const result: EligibilityOffer[] = [];
    categoryCodeInfo.forEach((categoryCodeInfo) => {
        const offer = offers.find((offer) => {
            return (
                offer.rangeNg === categoryCodeInfo.rangeNg &&
                offer.commercialSeries === categoryCodeInfo.commercialSeries &&
                offer.technology === categoryCodeInfo.technology
            );
        });

        if (offer) {
            result.push(offer);
        }
    });
    return result;
}

function hasOffersBigDiscount(offers: EligibilityOffer[]): boolean {
    return offers.some((offer) =>
        offer.discounts.some((discount) => discount.marketingTypes.includes(MarketingType.HOME)),
    );
}

interface EligibilityResultTabsProps {
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
    shortList?: ShortListContext;
    categoryCodeInfo: CategoryCodeInfo[];
    onAddToCart: (gencode: string) => void;
}

export function EligibilityResultTabs({
    results,
    journeyConfig,
    shortList,
    categoryCodeInfo,
    onAddToCart,
}: Readonly<EligibilityResultTabsProps>) {
    const isPackAvailable = useMemo(
        () => journeyConfig.showPackTabs && !!results.offers?.FTTH,
        [journeyConfig, results],
    );

    const [tabs, defaultTab] = useMemo(() => {
        const technologies = Object.keys(results.offers)
            .map((technology) => {
                return { name: technology, label: getTechnologyName(technology as TECHNOLOGY, results)! };
            })
            .filter((technology) => !!technology.label)
            .sort((a, b) => tabsOrder.indexOf(a.name) - tabsOrder.indexOf(b.name));

        if (isPackAvailable) {
            technologies.push({ name: 'PACK', label: 'Pack' });
        }

        const shortListTechnology = normalizeTechnology(shortList?.technology);

        const shortListTab = technologies.find((t) => t.name === shortListTechnology);

        const defaultTab = shortListTab ?? technologies.find((t) => t.name === results.technology) ?? technologies[0];

        if (shortListTab && shortList) {
            defaultTab!.name = shortList.technology.toUpperCase() as TECHNOLOGY;
        }

        return [technologies, defaultTab];
    }, [results, journeyConfig, shortList]);

    const [currentTab, setCurrentTab] = useState<TabItem | undefined>(defaultTab);

    const onTabChange = (activeTab: TabItem) => {
        setCurrentTab(activeTab);
    };

    return (
        <>
            {!shortList?.focus && tabs.length > 1 && <Tabs items={tabs} onClick={onTabChange} activeTab={currentTab} />}
            {currentTab?.name === 'PACK' ? (
                <SSIBlock tagId='pack-cards' url={partials.MARKETING.ELIGIBILITY_PACK} />
            ) : (
                <OfferList
                    currentTab={currentTab as { name: TECHNOLOGY; label: string }}
                    journeyConfig={journeyConfig}
                    results={results}
                    shortList={shortList}
                    categoryCodeInfo={categoryCodeInfo}
                    onAddToCart={onAddToCart}
                />
            )}
            {currentTab?.name === TECHNOLOGY.FTTH && (
                <SSIBlock tagId='post-elig-emerch' url={partials.MARKETING.ELIGIBILITY_FTTH} />
            )}
        </>
    );
}

interface OfferListProps {
    currentTab?: { name: TECHNOLOGY; label: string };
    journeyConfig: JourneyConfigType;
    results: EligibilityResults;
    shortList?: ShortListContext;
    categoryCodeInfo: CategoryCodeInfo[];
    onAddToCart: (gencode: string) => void;
}

function OfferList({
    currentTab,
    journeyConfig,
    results,
    shortList,
    categoryCodeInfo,
    onAddToCart,
}: Readonly<OfferListProps>) {
    const [shouldShowSecondaryOffers, setShouldShowSecondaryOffers] = useState<boolean>(false);
    const [marketingsState, _setMarketingsState] = useMarketings();
    const [session, setSession] = useState<Session | null>(null);

    const user = session?.user as UserType | undefined;

    const selectedPackProductSessionVariable = getSessionVariable(ELIGIBILITY_STORAGE_KEY.PACK_PRODUCT);
    const selectedPackProduct = !!selectedPackProductSessionVariable
        ? (JSON.parse(selectedPackProductSessionVariable) as StoredHomepackProduct)
        : null;

    const initialMarketingsState = { ...marketingsState, [MarketingType.MOBILE]: true };

    useEffect(() => {
        emitPriceContextChangeEvent(initialMarketingsState);
        getServerSession()
            .then((_session) => {
                setSession(_session);
            })
            .catch(() => setSession(null));
    }, []);

    const [primaryOffers, secondaryOffers, hasPrimaryOfferHat, hasSecondaryOffersHat] = useMemo(() => {
        if (!currentTab) {
            return [[], []];
        }
        if (!shortList || currentTab.name !== shortList.technology.toUpperCase()) {
            const offers = results.offers[currentTab.name]!.filter((offer) => !offer.isHidden);
            return [
                sortOffersWithCategoryOrder(categoryCodeInfo, offers),
                [],
                offers.some((offer) => offer.hat !== undefined),
                false,
            ];
        }

        let primaryOffers: EligibilityOffer[] = [];
        let secondaryOffers: EligibilityOffer[] = [];
        let hasPrimaryOfferHat: boolean = false;
        let hasSecondaryOffersHat: boolean = false;

        const allOffers = Object.values(results.offers).flat();

        allOffers.forEach((offer) => {
            if (
                offer.isSameRangeAndPlay(shortList.rangeNgCommercialSeries, '') ||
                offer.isSameOldRange(shortList.rangeNgCommercialSeries)
            ) {
                if (!offer.isHiddenInFocus) {
                    primaryOffers.push(offer);

                    if (offer.hat) {
                        hasPrimaryOfferHat = true;
                    }
                }
            } else {
                if (!offer.isHidden) {
                    secondaryOffers.push(offer);

                    if (offer.hat) {
                        hasSecondaryOffersHat = true;
                    }
                }
            }
        });

        if (primaryOffers.length === 0 && secondaryOffers.length > 0) {
            primaryOffers = sortOffersWithCategoryOrder(categoryCodeInfo, secondaryOffers);
            secondaryOffers = [];
        } else {
            secondaryOffers = sortOffersWithCategoryOrder(categoryCodeInfo, secondaryOffers);
        }

        return [primaryOffers, secondaryOffers, hasPrimaryOfferHat, hasSecondaryOffersHat];
    }, [results, currentTab, shortList]);

    if (!currentTab) {
        return null;
    }

    return (
        <>
            {!user && hasOffersBigDiscount(primaryOffers) && (
                <PromoConfigurator
                    isFullwidth
                    marketingsState={initialMarketingsState}
                    setMarketingsState={(marketings) => {
                        const newMarketingsState = _setMarketingsState(marketings);
                        emitPriceContextChangeEvent(newMarketingsState);
                        return newMarketingsState;
                    }}
                />
            )}
            <Spacer size={SpacerSize.FOUR} />
            <Columns align={Alignable.ALIGNED_CENTER} gap={2} multiline>
                {shortList?.focus &&
                    journeyConfig.showComboProductSlug &&
                    selectedPackProduct &&
                    journeyConfig.showComboProductSlug.includes(selectedPackProduct.slug) && (
                        <>
                            <Column size={5}>
                                <SelectedPackProductCard selectedProduct={selectedPackProduct} />
                            </Column>
                            <Column className='has-text-centered m-auto'>
                                <Icon name={IconName.PLUS_CIRCLE} size={IconSize.LARGE} />
                            </Column>
                        </>
                    )}
                {primaryOffers.map((offer) => (
                    <Column
                        key={offer.sku}
                        mobileSize={12}
                        tabletSize={6}
                        desktopSize={4}
                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                    >
                        <RocCard
                            plan={offer.mapToFaiType()}
                            cta={{
                                onClick: () => onAddToCart(offer.sku),
                                text: 'Ajouter au panier',
                                color: ButtonVariant.CONVERSION,
                            }}
                            hasSiblingHat={hasPrimaryOfferHat}
                            user={user}
                        />
                    </Column>
                ))}
            </Columns>

            {secondaryOffers.length > 0 && (
                <>
                    <Spacer size={SpacerSize.EIGHT} />
                    <Columns marginless>
                        <Column className='has-text-centered'>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                iconName={shouldShowSecondaryOffers ? IconName.ARROW_UP : IconName.ARROW_DOWN}
                                onClick={() => setShouldShowSecondaryOffers((oldValue) => !oldValue)}
                            >
                                {shouldShowSecondaryOffers ? 'Masquer les offres' : 'Afficher les autres offres'}
                            </Button>
                        </Column>
                    </Columns>
                    {shouldShowSecondaryOffers && (
                        <>
                            {!user && hasOffersBigDiscount(secondaryOffers) && !hasOffersBigDiscount(primaryOffers) && (
                                <>
                                    <Spacer size={SpacerSize.SIX} />
                                    <PromoConfigurator
                                        isFullwidth
                                        marketingsState={initialMarketingsState}
                                        setMarketingsState={(marketings) => {
                                            const newMarketingsState = _setMarketingsState(marketings);
                                            emitPriceContextChangeEvent(newMarketingsState);
                                            return newMarketingsState;
                                        }}
                                    />
                                </>
                            )}
                            <Spacer size={SpacerSize.SIX} />
                            <Columns multiline align={Alignable.ALIGNED_CENTER} gap={2}>
                                {secondaryOffers.map((offer) => (
                                    <Column
                                        key={offer.sku}
                                        mobileSize={12}
                                        tabletSize={6}
                                        desktopSize={4}
                                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                                    >
                                        <RocCard
                                            plan={offer.mapToFaiType()}
                                            cta={{
                                                onClick: () => onAddToCart(offer.sku),
                                                text: 'Ajouter au panier',
                                                color: ButtonVariant.CONVERSION,
                                            }}
                                            hasSiblingHat={hasSecondaryOffersHat}
                                            user={user}
                                        />
                                    </Column>
                                ))}
                            </Columns>
                        </>
                    )}
                </>
            )}
        </>
    );
}
